/**
 * Session Timeout Hook
 * Monitors user activity and automatically logs out after inactivity
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { clearAllAuthData } from '../utils/tokenUtils';

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before timeout

export function useSessionTimeout(userType = 'user') {
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const resetTimer = () => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    // Set warning timer (5 minutes before timeout)
    warningTimeoutRef.current = setTimeout(() => {
      const timeLeft = Math.ceil((INACTIVITY_TIMEOUT - WARNING_TIME) / 1000 / 60);
      const confirmed = window.confirm(
        `Your session will expire in ${timeLeft} minutes due to inactivity. Click OK to continue your session.`
      );
      
      if (confirmed) {
        resetTimer(); // Reset timer if user confirms
      }
    }, INACTIVITY_TIMEOUT - WARNING_TIME);

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      // Session expired due to inactivity
      clearAllAuthData();
      
      if (userType === 'user') {
        authService.userLogout();
        navigate('/signin', { 
          replace: true,
          state: { message: 'Your session has expired due to inactivity. Please log in again.' }
        });
      } else if (userType === 'admin') {
        authService.adminLogout();
        navigate('/admin/login', { 
          replace: true,
          state: { message: 'Your session has expired due to inactivity. Please log in again.' }
        });
      }
    }, INACTIVITY_TIMEOUT);
  };

  useEffect(() => {
    // List of events that indicate user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Add event listeners for user activity
    const handleActivity = () => {
      resetTimer();
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, true);
    });

    // Initialize timer
    resetTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity, true);
      });
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [navigate, userType]);

  return { resetTimer };
}

